/**
 * The re-verification a firm does before something it cannot undo.
 *
 * Two steps and one rule: **the assertion is handed to the caller and never
 * kept.** `onVerified` receives it, uses it for exactly one request, and the
 * dialog closes. Nothing here writes it anywhere — an assertion that outlived
 * its action would be a re-verification that re-verified nothing.
 *
 * The dialog says **what it is about to do**, in the firm's own words, because
 * the whole content of a step-up prompt is *which* irreversible thing is about
 * to happen. "Confirm your identity" with no object is a prompt people learn to
 * click through.
 *
 * **It lives here, and not under `routes/Billing/`, because three screens need
 * it and only one of them knew.** Wave 4C built it for invoice issue after
 * reading the routes off the running stack (`core-auth`, not `core-mfa`); Wave
 * 4B's document share and Wave 4D's publish were written before that and each
 * concluded, correctly from what it could see, that a browser had no way to
 * obtain an assertion. So the share dialog carried a `requestAssertion()` that
 * returned `null` and the publish button offered a link to the accounts app —
 * two gates that could not be passed, on a product where both acts are
 * irreversible. Nothing was wrong with either session's reasoning; what was
 * missing was one of them knowing what another had found. The Wave 4 close
 * moved this file and wired the other two to it.
 *
 * **Try first, then challenge** — C27 (spec §10.19), ADR-0056 §7, UAT reports
 * #9 and #16. Issue, credit, refund and share opened this dialog *before*
 * sending anything, so a person was asked for a code the service might not
 * have wanted. Publish never did: it sends, and opens the dialog only on
 * `STEPUP_REQUIRED`. `useStepUp` below is that pattern, once, for every screen
 * — four hand-written copies of it is how the four drifted from publish in the
 * first place. **The gate itself is unchanged** (the owner: *"Keep it; fix the
 * friction"*): the assertion is still minted per action and used on exactly one
 * retry, and nothing reuses one.
 */

import { Alert, Button, Dialog, OTPInput } from '@fiveox/ui'
import { useCallback, useState } from 'react'

import { ApiError } from '../lib/api'
import {
  METHOD_LABELS,
  STEPUP_METHODS,
  type StepUpMethod,
  assertStepUp,
  sendCode,
} from '../lib/stepup'

export function StepUpDialog({
  open,
  title,
  what,
  onClose,
  onVerified,
}: {
  open: boolean
  title: string
  /** One sentence naming the irreversible thing. */
  what: string
  onClose: () => void
  onVerified: (assertion: string) => Promise<void> | void
}) {
  const [method, setMethod] = useState<StepUpMethod>('email')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reset = () => {
    setSent(false)
    setCode('')
    setError(null)
    setBusy(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  const send = async (chosen: StepUpMethod) => {
    setBusy(true)
    setError(null)
    setMethod(chosen)
    try {
      if (chosen !== 'totp') await sendCode(chosen)
      setSent(true)
    } catch (err) {
      // Core's own words. The method vocabulary is not published and a person
      // may simply not hold this factor — telling them "something went wrong"
      // when core said "no mobile number on file" is the difference between a
      // fixable problem and a support ticket.
      setError(err instanceof ApiError ? err.message : 'That code could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  const verify = async () => {
    setBusy(true)
    setError(null)
    try {
      const assertion = await assertStepUp(method, code)
      await onVerified(assertion)
      reset()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code was not accepted.')
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} title={title}>
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">{what}</p>
        {error && <Alert tone="error">{error}</Alert>}

        {!sent ? (
          <div className="space-y-2">
            <p className="text-sm">How would you like to confirm it is you?</p>
            {STEPUP_METHODS.map((option) => (
              <Button
                key={option}
                variant="secondary"
                className="w-full justify-start"
                disabled={busy}
                onClick={() => void send(option)}
              >
                {METHOD_LABELS[option]}
              </Button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">
              {method === 'totp'
                ? 'Enter the current code from your authenticator app.'
                : 'Enter the six-digit code we just sent you.'}
            </p>
            <OTPInput value={code} onChange={setCode} autoFocus disabled={busy} />
            <div className="flex items-center gap-2">
              <Button onClick={() => void verify()} disabled={busy || code.length < 6}>
                Confirm
              </Button>
              <Button variant="ghost" onClick={reset} disabled={busy}>
                Use a different method
              </Button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}

/** An action that may be gated: called once with no assertion, and — only if
 *  the service answered `STEPUP_REQUIRED` — once more with a fresh one. */
export type GatedAction = (assertion?: string) => Promise<void>

/** The refusals that belong to the re-verification itself. Shown in the
 *  dialog, which stays open so the person can try another code; anything else
 *  is the action's own refusal and goes back to the screen. */
const STEPUP_CODES = new Set(['STEPUP_REQUIRED', 'STEPUP_INVALID'])

/**
 * Try first, then challenge — the publish pattern (`routes/Site/index.tsx`),
 * for every gated action. C27, ADR-0056 §7.
 *
 * `attempt(action)` runs the action with no assertion. If it goes through, that
 * is the end of it (an environment that does not enforce step-up asks nobody
 * for a code). If the service answers `STEPUP_REQUIRED`, the action is held and
 * `dialog.open` turns true; `dialog.onVerified` runs it **once** with the
 * assertion the person just earned. Any other refusal — from the first try or
 * from the retry — is handed to `onError` and the dialog closes, because a
 * *"Set your firm's state"* in a box asking for a six-digit code reads as a
 * wrong code.
 *
 * **What is held is the action, never the assertion.** The assertion reaches
 * the action as an argument, rides on one request and goes out of scope —
 * `lib/stepup.ts`'s rule, unchanged. A refused retry asks for a new code.
 */
export function useStepUp(onError: (err: unknown) => void) {
  const [held, setHeld] = useState<GatedAction | null>(null)

  const attempt = useCallback(
    async (action: GatedAction): Promise<void> => {
      try {
        await action(undefined)
      } catch (err) {
        if (err instanceof ApiError && err.code === 'STEPUP_REQUIRED') {
          // A function in state is set through the updater form, or React
          // would call it as one.
          setHeld(() => action)
          return
        }
        onError(err)
      }
    },
    [onError],
  )

  const onVerified = useCallback(
    async (assertion: string) => {
      if (!held) return
      try {
        await held(assertion)
        setHeld(null)
      } catch (err) {
        if (err instanceof ApiError && STEPUP_CODES.has(err.code)) throw err
        setHeld(null)
        onError(err)
      }
    },
    [held, onError],
  )

  return {
    attempt,
    /** True while the dialog is asking — the screen's own dialog steps aside,
     *  because two kit dialogs at once each read a click in the other as an
     *  outside click. */
    challenging: held !== null,
    dialog: { open: held !== null, onClose: () => setHeld(null), onVerified },
  }
}
